import { Checkbox, TextInput } from '@mantine/core';
import { useEffect } from 'react';
import { useFillOutRevisionDataProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionDataProvider';
import { DateInput } from 'components/inputs/DateInput';
import { RevisionFieldProps } from 'components/forms/revision/fill-out/types';
import { IFormInputSpecConstant } from 'pages/revisions-module/template-editor/editors/form/types';

/**
 * Constant field for the revision form.
 */
export default function RevisionFieldConstant({ spec, name }: RevisionFieldProps<IFormInputSpecConstant>) {
  const { form } = useFillOutRevisionDataProvider();
  const { value: storedValue } = form.getInputProps(name);

  const normalizedValue =
    spec.constantType === 'bool'
      ? !!spec.value
      : spec.constantType === 'number'
      ? typeof spec.value === 'number'
        ? spec.value
        : Number(spec.value)
      : String(spec.value ?? '');

  useEffect(() => {
    // Use Number.isNaN check to handle the edge case where both values are NaN —
    // a plain !== comparison would always be true for NaN, causing an infinite loop.
    const bothNaN = Number.isNaN(storedValue) && Number.isNaN(normalizedValue);

    if (!bothNaN && storedValue !== normalizedValue) {
      form.setFieldValue(name, normalizedValue);
    }
  }, [form, name, normalizedValue, storedValue]);

  if (spec.constantType === 'bool') {
    return (
      <Checkbox
        size="lg"
        name={name}
        label={spec.label}
        required={spec.required}
        disabled
        readOnly
        checked={!!normalizedValue}
        onChange={() => undefined}
      />
    );
  }

  if (spec.constantType === 'date') {
    return (
      <DateInput
        size="lg"
        name={name}
        label={spec.label}
        required={spec.required}
        readOnly
        value={String(normalizedValue)}
      />
    );
  }

  return (
    <TextInput
      size="lg"
      name={name}
      label={spec.label}
      required={spec.required}
      readOnly
      value={String(normalizedValue)}
    />
  );
}
